import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CpuIcon, SpinnerGapIcon, GlobeIcon, ChartLineUpIcon, XLogoIcon, TelegramLogoIcon, StarIcon } from "@phosphor-icons/react";
import { fetchTechTokens } from "../lib/api";
import { fmtUsd } from "../lib/format";
import { QuickBuyLink } from "./TradeButton";
import { useWatchlist } from "../context/WatchlistContext";
import { useTrash, useCommunityTrash } from "../lib/useTrash";
import { TrashButton } from "./TrashButton";
import { InfoTip } from "./InfoTip";
import { filterByAge, ageLabel } from "./AgeFilter";
import { useGlobalAge } from "../context/AgeFilterContext";

const CYAN = "#22D3EE";
const scoreColor = (s) => (s >= 80 ? CYAN : s >= 60 ? "#4ADE80" : s >= 45 ? "#F0C000" : "#888");

const WEB_STATUS = {
  ok: { label: "🌐 Live", term: "websiteLive", color: "#4ADE80", bg: "rgba(74,222,128,0.15)", bd: "rgba(74,222,128,0.5)" },
  parked: { label: "🅿️ Parked", term: "websiteParked", color: "#F0A020", bg: "rgba(240,160,32,0.15)", bd: "rgba(240,160,32,0.55)" },
  dead: { label: "💀 Dead site", term: "websiteDead", color: "#FF3366", bg: "rgba(255,51,102,0.18)", bd: "rgba(255,51,102,0.6)" },
};

const WebStatusBadge = ({ status }) => {
  const s = WEB_STATUS[status];
  if (!s) return null;
  return (
    <InfoTip term={s.term}>
      <span data-testid="tech-web-status" className="text-[9px] font-mono px-1.5 py-0.5 font-bold tracking-wide" style={{ background: s.bg, border: `1px solid ${s.bd}`, color: s.color }}>
        {s.label}
      </span>
    </InfoTip>
  );
};

const TechCard = ({ c }) => {
  const { isWatched, toggle } = useWatchlist();
  const watched = isWatched(c.mint);
  const tw = (c.socials || []).find((s) => s.type === "twitter");
  const tg = (c.socials || []).find((s) => s.type === "telegram");
  const flagged = c.websiteStatus === "dead" || c.websiteStatus === "parked";
  return (
    <div data-testid={`tech-card-${c.mint}`} className={`flex flex-col gap-2.5 border p-3.5 transition-colors hover:bg-white/[0.03] ${flagged ? "border-danger/30 opacity-90" : "border-white/10"}`} style={{ background: `linear-gradient(135deg, ${CYAN}0A, transparent 60%)` }}>
      <div className="flex items-center gap-2.5">
        {c.image ? (
          <img src={c.image} alt="" className="w-9 h-9 object-cover border border-white/10 shrink-0" onError={(e) => (e.target.style.display = "none")} />
        ) : (
          <div className="w-9 h-9 flex items-center justify-center border border-white/10 shrink-0 text-muted-foreground"><CpuIcon size={16} /></div>
        )}
        <div className="min-w-0 flex-1">
          <Link to={`/coin/${c.mint}`} className="font-heading font-bold truncate hover:text-neon transition-colors">${c.symbol || "token"}</Link>
          <div className="font-mono text-[10px] text-muted-foreground truncate">{fmtUsd(c.marketCap)} mc · {c.ageHours != null ? `${Math.round(c.ageHours)}h` : "—"} · conv {c.conviction}</div>
        </div>
        <InfoTip term="techScore" side="left">
          <div className="text-right shrink-0">
            <div className="font-heading font-black text-2xl leading-none" style={{ color: scoreColor(c.techScore) }}>{c.techScore}</div>
            <div className="font-mono text-[8px] uppercase tracking-wider text-muted-foreground">tech</div>
          </div>
        </InfoTip>
      </div>

      <div className="flex flex-wrap gap-1">
        {c.keyword && <span className="text-[9px] font-mono px-1.5 py-0.5" style={{ background: `${CYAN}18`, border: `1px solid ${CYAN}66`, color: CYAN }}>🔬 {c.keyword}</span>}
        <WebStatusBadge status={c.websiteStatus} />
        {c.lpLocked && <InfoTip term="lpLocked"><span className="text-[9px] font-mono px-1.5 py-0.5 font-bold" style={{ background: "rgba(74,222,128,0.15)", border: "1px solid rgba(74,222,128,0.6)", color: "#4ADE80" }}>🔒 LP</span></InfoTip>}
        {c.lpUnlocked && <InfoTip term="lpUnlocked"><span className="text-[9px] font-mono px-1.5 py-0.5 bg-danger/20 border border-danger/60 text-danger font-bold">🔓 LP</span></InfoTip>}
        {(c.reasons || []).slice(0, 2).filter((r) => !r.includes("website")).map((r, i) => (
          <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 bg-white/5 border border-white/10 text-muted-foreground">{r}</span>
        ))}
      </div>

      <div className="flex items-center gap-1 pt-0.5">
        {c.website && (
          <a href={c.website} target="_blank" rel="noreferrer" title={c.website} data-testid={`tech-web-${c.mint}`} className={`flex items-center gap-1 text-[11px] font-mono px-2 py-1.5 border transition-colors ${c.websiteStatus === "dead" ? "border-danger/40 text-danger/80 line-through" : c.websiteStatus === "parked" ? "border-[#F0A020]/40 text-[#F0A020]/90" : "border-white/15 hover:border-[#22D3EE]/50 hover:text-[#22D3EE]"}`}>
            <GlobeIcon size={13} weight="bold" /> site
          </a>
        )}
        {tw && <a href={tw.url} target="_blank" rel="noreferrer" title="X" className="p-1.5 text-muted-foreground hover:text-white transition-colors"><XLogoIcon size={14} weight="fill" /></a>}
        {tg && <a href={tg.url} target="_blank" rel="noreferrer" title="Telegram" className="p-1.5 text-muted-foreground hover:text-[#229ED9] transition-colors"><TelegramLogoIcon size={15} weight="fill" /></a>}
        <div className="flex-1" />
        <QuickBuyLink coin={{ address: c.mint, url: c.url }} testid={`tech-buy-${c.mint}`} />
        <button onClick={() => toggle(c.mint)} title={watched ? "Remove from watchlist" : "Save to watchlist"} data-testid={`tech-watch-${c.mint}`} className="p-1.5 hover:bg-white/5 transition-colors">
          <StarIcon size={15} weight={watched ? "fill" : "regular"} color={watched ? "#F5A623" : "#666"} />
        </button>
        <Link to={`/coin/${c.mint}`} title="Chart & score" data-testid={`tech-chart-${c.mint}`} className="p-1.5 text-muted-foreground hover:text-neon transition-colors"><ChartLineUpIcon size={15} weight="bold" /></Link>
        <TrashButton mint={c.mint} symbol={c.symbol} testid={`tech-trash-${c.mint}`} />
      </div>
    </div>
  );
};

export const TechTokens = () => {
  const { data, isLoading } = useQuery({ queryKey: ["techTokens"], queryFn: fetchTechTokens, refetchInterval: 120000, placeholderData: (p) => p });
  const { age } = useGlobalAge();
  const { isTrashed } = useTrash();
  const { isCommunityScam } = useCommunityTrash();
  const allTokens = (data?.tokens || []).filter((c) => !isTrashed(c.mint) && !isCommunityScam(c.mint));
  const fresh6 = allTokens.filter((c) => (c.ageHours ?? Infinity) < 6).length;
  const tokens = filterByAge(allTokens, age);

  return (
    <div className="space-y-3 border-t border-white/10 pt-8" data-testid="tech-tokens">
      <div className="flex items-center gap-2.5 flex-wrap">
        <CpuIcon size={24} weight="fill" style={{ color: CYAN }} />
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter leading-none">
          TECH <span style={{ color: CYAN }}>TOKENS</span>
        </h2>
        {fresh6 > 0 && (
          <span data-testid="tech-fresh-badge" title={`${fresh6} brand-new tech token${fresh6 === 1 ? "" : "s"} under 6h old`} className="flex items-center gap-1 text-[10px] font-mono font-bold px-2 py-1 tracking-wide animate-pulse" style={{ background: `${CYAN}1A`, border: `1px solid ${CYAN}`, color: CYAN }}>
            🆕 {fresh6} under 6h
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground max-w-2xl">
        Tokens that look like a <span style={{ color: CYAN }} className="font-semibold">real product, not a pure meme</span> — they ship a
        website, a real community and utility signals, and tend to grind up instead of dumping. Ranked by a Tech Score
        (website + socials + utility keywords + survival). We live-check each site — <span style={{ color: "#4ADE80" }}>🌐 Live</span>,{" "}
        <span style={{ color: "#F0A020" }}>🅿️ Parked</span> or <span className="text-danger">💀 Dead</span>. Always DYOR.
      </p>
      {isLoading ? (
        <div className="flex justify-center py-14"><SpinnerGapIcon size={28} className="animate-spin" style={{ color: CYAN }} /></div>
      ) : allTokens.length === 0 ? (
        <div className="border border-white/10 bg-card py-14 text-center text-muted-foreground font-mono text-sm px-6">No tech tokens on the radar right now — check back soon.</div>
      ) : tokens.length === 0 ? (
        <div className="border border-white/10 bg-card py-14 text-center text-muted-foreground font-mono text-sm px-6">No tech tokens under {ageLabel(age)} old right now — widen the age filter at the top.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
          {tokens.map((c) => <TechCard key={c.mint} c={c} />)}
        </div>
      )}
    </div>
  );
};
